import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Star, Palette, Search } from "lucide-react";
// Removed mock fallback to ensure category-pure lists
import { listTemplatesMeta, type TemplateMeta } from "../api/templates";
import { PreviewCard, resolveVariant, getDefaultAccent } from "./templates/PreviewCard";
import { PreviewCard as MetaPreviewCard } from "./templates/MetaPreviewCard";
import { ensureTemplateVariant } from "../lib/templates/normalize";
import { DEFAULT_COVER } from '@/constants/media';
import { TemplatePreviewDialog } from "./templates/TemplatePreviewDialog";

interface TemplateSelectionProps {
  userData: {
    name: string;
    email: string;
    phone: string;
    position: string;
    company: string;
    gender: string;
    age: string;
    location: string;
  website?: string;
  bio?: string;
    avatarUrl?: string; // NEW: passed from signup
    coverUrl?: string;  // NEW: passed from signup
  };
  // Accept either a simple static id ("1".."5") or a richer payload with dynamic template reference
  onTemplateSelect: (payload: { templateId?: string; templateMetaId?: string; componentKey?: string; variant?: string } | string) => void;
  onBack: () => void;
  embedded?: boolean;
}

type TemplateCard = {
  id: string;
  name: string;
  description?: string;
  componentKey?: string;
};

const ACCENTS = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#06B6D4", "#8B5CF6", "#22C55E", "#0EA5E9"];
const isPopular = (t: TemplateCard) => /elegant|gold(en)?|classic/i.test(t.name) || t.id === "2";

export const TemplateSelection: React.FC<TemplateSelectionProps> = ({ userData, onTemplateSelect, onBack, embedded = false }) => {
  // Persist the incoming user data so a route refresh on this page doesn't drop assets
  React.useEffect(() => {
    try { localStorage.setItem('template_selection_user', JSON.stringify(userData)); } catch {}
  }, [userData]);

  // Normalize a value into an absolute URL when possible; do not prefix frontend/public or upload paths incorrectly
  const normalizeAsset = (val?: string) => {
    if (!val) return undefined as undefined | string;
    const s = String(val);
    if (/^(https?:\/\/|blob:|data:)/i.test(s)) return s;
    // Leave frontend static assets, and server uploads, as-is if they are rooted paths
    if (s.startsWith('/images/') || s.startsWith('/assets/') || s.startsWith('/uploads/')) return s;
    // Handle relative uploads without leading slash
    if (s.startsWith('uploads/')) return `/${s}`;
    const base = import.meta.env.VITE_APP_BASE_URL || import.meta.env.VITE_API_BASE_URL || '';
    return s.startsWith('/') && base ? `${String(base).replace(/\/$/, '')}${s}` : s;
  };
  // Category selection (persisted)
  const storageKey = `template_selection:${(userData.email || "").toLowerCase()}`;
  const storageCategoryKey = `template_selection_category:${(userData.email || "").toLowerCase()}`;
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [query, setQuery] = useState("");
  const [peekOpen, setPeekOpen] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState<string | null>(null);
  const [category, setCategory] = useState<'business' | 'artist'>(() => {
    try { return (localStorage.getItem(storageCategoryKey) as any) || 'business'; } catch { return 'business'; }
  });

  // Server templates state
  const [serverTemplates, setServerTemplates] = useState<TemplateCard[]>([]);
  const [serverTemplatesMeta, setServerTemplatesMeta] = useState<Record<string, TemplateMeta>>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [usedFallback, setUsedFallback] = useState(false);

  // Prime UI instantly from local cache if present (no network wait)
  const primeFromCache = React.useCallback((cat: 'business'|'artist') => {
    try {
      const cached = localStorage.getItem('templates_cache_meta');
      if (!cached) return false;
      const obj = JSON.parse(cached) as Record<string, TemplateMeta>;
      const all = Object.values(obj || {}).filter(Boolean);
      const catLower = cat.toLowerCase();
      const filtered = all.filter((m: any) => {
        const c = String(m?.category || '').toLowerCase();
        const catNorm = (c === 'business' || c === 'artist') ? c : (c.includes('business') ? 'business' : c.includes('artist') ? 'artist' : '');
        return !catNorm || catNorm === catLower;
      });
      if (!filtered.length) return false;
      const mapped: TemplateCard[] = filtered.map((it: TemplateMeta) => ({ id: it.id, name: it.name, description: it.description, componentKey: it.componentKey }));
      const asMap: Record<string, TemplateMeta> = {};
      filtered.forEach((m) => { if (m?.id) asMap[m.id] = m; });
      setServerTemplates(mapped);
      setServerTemplatesMeta(asMap);
      setUsedFallback(true);
      return true;
    } catch { return false; }
  }, []);

  // Fetch templates from server when category changes or when admin signals an update via storage
  const refreshTemplates = React.useCallback(async (cat: 'business'|'artist') => {
    // Do not clear existing list; we want instant UI. Mark loading but keep current tiles.
    setLoading(true);
    setLoadError(null);
    setUsedFallback(false);
    try {
      // Fetch without activeOnly filter so 'uploaded' templates are included; we'll exclude only explicit 'inactive' on client
      const items = await listTemplatesMeta({ category: cat });
      // Normalize and enforce category; be permissive on status: exclude only explicit 'inactive'
      const normalized = (items || []).filter(Boolean).filter((it: any) => {
        const status = String(it?.status || '').toLowerCase();
        const notInactive = status !== 'inactive'; // allow 'uploaded' and 'active'
        // category match (server may send title case or extended label)
        const c = String(it?.category || '').toLowerCase();
        const catNorm = (c === 'business' || c === 'artist') ? c : (c.includes('business') ? 'business' : c.includes('artist') ? 'artist' : '');
        return notInactive && (!catNorm || catNorm === cat);
      }) as TemplateMeta[];
      // Primary path: server returned usable templates
      if (normalized.length) {
        const mapped: TemplateCard[] = normalized.map((it: TemplateMeta) => ({ id: it.id, name: it.name, description: it.description, componentKey: it.componentKey }));
        const asMap: Record<string, TemplateMeta> = {};
        normalized.forEach((m) => { if (m?.id) asMap[m.id] = m; });
        setServerTemplates(mapped);
        setServerTemplatesMeta(asMap);
        // Cache for offline
        try {
          const existing = JSON.parse(localStorage.getItem('templates_cache_meta') || '{}');
          localStorage.setItem('templates_cache_meta', JSON.stringify({ ...existing, ...asMap }));
        } catch {}
        return;
      }

      // If empty, try local cached meta saved by admin/customize flows
      let cachedList: TemplateMeta[] = [];
      try {
        const cached = localStorage.getItem('templates_cache_meta');
        if (cached) {
          const obj = JSON.parse(cached) as Record<string, TemplateMeta>;
          cachedList = Object.values(obj || {}).filter(Boolean);
        }
      } catch {}
      if (cachedList.length) {
        const catLower = cat.toLowerCase();
        const filtered = cachedList.filter((m: any) => {
          const c = String(m?.category || '').toLowerCase();
          const catNorm = (c === 'business' || c === 'artist') ? c : (c.includes('business') ? 'business' : c.includes('artist') ? 'artist' : '');
          return !catNorm || catNorm === catLower;
        });
        if (filtered.length) {
          const mapped: TemplateCard[] = filtered.map((it: TemplateMeta) => ({ id: it.id, name: it.name, description: it.description, componentKey: it.componentKey }));
          const asMap: Record<string, TemplateMeta> = {};
          filtered.forEach((m) => { if (m?.id) asMap[m.id] = m; });
          setServerTemplates(mapped);
          setServerTemplatesMeta(asMap);
          setUsedFallback(true);
          return;
        }
      }

      // Final fallback: show core built-ins so user can proceed even when offline (both categories)
      if (cat === 'business' || cat === 'artist') {
        setServerTemplates([
          { id: 'builtin-1', name: 'Professional Classic', description: 'Clean, balanced layout with strong typography.', componentKey: 'professional-classic' },
          { id: 'builtin-2', name: 'Elegant Looking', description: 'Refined style with subtle accents and contrast.', componentKey: 'elegant-looking' },
          { id: 'builtin-3', name: 'Modern and Clean', description: 'Minimal design focused on clarity and structure.', componentKey: 'modern-clean' },
          { id: 'builtin-4', name: 'Golden View', description: 'Luxury golden theme with premium styling.', componentKey: 'golden-view' },
          { id: 'builtin-5', name: 'Classic Modular', description: 'Modular component-based design with clean sections.', componentKey: 'classic-modular' },
        ]);
        setServerTemplatesMeta({});
        setUsedFallback(true);
      }
    } catch (e: any) {
      setLoadError(e?.name === 'AbortError' ? 'Template service timed out' : (e?.message || 'Failed to load templates'));
      // Attempt cached meta, else Business built-ins
      let cachedList: TemplateMeta[] = [];
      try {
        const cached = localStorage.getItem('templates_cache_meta');
        if (cached) {
          const obj = JSON.parse(cached) as Record<string, TemplateMeta>;
          cachedList = Object.values(obj || {}).filter(Boolean);
        }
      } catch {}
      const catLower = cat.toLowerCase();
      const filtered = cachedList.filter((m: any) => {
        const c = String(m?.category || '').toLowerCase();
        const catNorm = (c === 'business' || c === 'artist') ? c : (c.includes('business') ? 'business' : c.includes('artist') ? 'artist' : '');
        return !catNorm || catNorm === catLower;
      });
      if (filtered.length) {
        const mapped: TemplateCard[] = filtered.map((it: TemplateMeta) => ({ id: it.id, name: it.name, description: it.description, componentKey: it.componentKey }));
        const asMap: Record<string, TemplateMeta> = {};
        filtered.forEach((m) => { if (m?.id) asMap[m.id] = m; });
        setServerTemplates(mapped);
        setServerTemplatesMeta(asMap);
        setUsedFallback(true);
      } else if (cat === 'business') {
        setServerTemplates([
          { id: 'builtin-1', name: 'Professional Classic', description: 'Clean, balanced layout with strong typography.', componentKey: 'professional-classic' },
          { id: 'builtin-2', name: 'Elegant Looking', description: 'Refined style with subtle accents and contrast.', componentKey: 'elegant-looking' },
          { id: 'builtin-3', name: 'Modern and Clean', description: 'Minimal design focused on clarity and structure.', componentKey: 'modern-clean' },
          { id: 'builtin-4', name: 'Golden View', description: 'Luxury golden theme with premium styling.', componentKey: 'golden-view' },
          { id: 'builtin-5', name: 'Classic Modular', description: 'Modular component-based design with clean sections.', componentKey: 'classic-modular' },
        ]);
        setServerTemplatesMeta({});
        setUsedFallback(true);
      } else if (cat === 'artist') {
        setServerTemplates([
          { id: 'builtin-1', name: 'Professional Classic', description: 'Clean, balanced layout with strong typography.', componentKey: 'professional-classic' },
          { id: 'builtin-2', name: 'Elegant Looking', description: 'Refined style with subtle accents and contrast.', componentKey: 'elegant-looking' },
          { id: 'builtin-3', name: 'Modern and Clean', description: 'Minimal design focused on clarity and structure.', componentKey: 'modern-clean' },
          { id: 'builtin-4', name: 'Golden View', description: 'Luxury golden theme with premium styling.', componentKey: 'golden-view' },
          { id: 'builtin-5', name: 'Classic Modular', description: 'Modular component-based design with clean sections.', componentKey: 'classic-modular' },
        ]);
        setServerTemplatesMeta({});
        setUsedFallback(true);
      } else {
        setServerTemplates([]);
        setServerTemplatesMeta({});
        setUsedFallback(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    // Prime from cache immediately for snappy UX, then refresh from network
    primeFromCache(category);
    (async () => { if (alive) await refreshTemplates(category); })();
    return () => { alive = false; };
  }, [category, refreshTemplates, primeFromCache]);

  // Listen for admin-side updates to templates and refresh list immediately
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'templates_updated_ts') {
        refreshTemplates(category);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [category, refreshTemplates]);

  // Use only server-provided templates for purity per category
  const uniqueTemplates = useMemo(() => {
    const m = new Map<string, TemplateCard>();
    // De-duplicate by normalized name to avoid any double entries (e.g., dynamic uploads vs built-ins)
    const seenByName = new Set<string>();
    serverTemplates.forEach((t) => {
      const nameKey = (t?.name || '').trim().toLowerCase().replace(/\s*\(dynamic\)$/i, '');
      if (!nameKey) return;
      if (seenByName.has(nameKey)) return;
      seenByName.add(nameKey);
      if (t?.id && !m.has(t.id)) m.set(t.id, t);
    });
    return Array.from(m.values());
  }, [serverTemplates]);

  

  // Accent: use template defaults by default, but allow quick override
  const [useDefaultAccents, setUseDefaultAccents] = useState(true);
  const [accentOverride, setAccentOverride] = useState<string>(ACCENTS[0]);

  // load persisted
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.templateId) setSelectedTemplate(String(parsed.templateId));
        if (typeof parsed?.useDefaultAccents === "boolean") setUseDefaultAccents(!!parsed.useDefaultAccents);
        if (parsed?.accentOverride) setAccentOverride(String(parsed.accentOverride));
      } else if (uniqueTemplates?.[0]?.id) {
        setSelectedTemplate(uniqueTemplates[0].id);
      }
    } catch {
      if (uniqueTemplates?.[0]?.id) setSelectedTemplate(uniqueTemplates[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, uniqueTemplates]);

  // persist
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ templateId: selectedTemplate, useDefaultAccents, accentOverride }));
    } catch {}
  }, [selectedTemplate, useDefaultAccents, accentOverride, storageKey]);

  // persist category
  useEffect(() => {
    try { localStorage.setItem(storageCategoryKey, category); } catch {}
  }, [category, storageCategoryKey]);

  const filteredTemplates = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = uniqueTemplates;
    if (!q) return base;
    return base.filter(
      (t) => t.name.toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q)
    );
  }, [query, uniqueTemplates]);

  const handleContinue = () => {
    if (!selectedTemplate) return;
    // If the selected id is one of the classic built-ins, pass it through unchanged
    if (/^[1-5]$/.test(selectedTemplate)) {
      return onTemplateSelect(selectedTemplate);
    }
    const meta = serverTemplatesMeta[selectedTemplate];
    // If the chosen id matches our built-in placeholders, map to static numeric ids directly
    const builtinMatch = selectedTemplate.match(/^builtin-(\d)$/);
    if (builtinMatch) {
      return onTemplateSelect(builtinMatch[1]);
    }

    // Heuristic mapping from componentKey to a static fallback templateId for resilience
    const key = String(meta?.componentKey || '').toLowerCase();
    let staticId: string = '1';
    if (/elegant/.test(key)) staticId = '2';
    else if (/modern/.test(key)) staticId = '3';
    else if (/gold/.test(key)) staticId = '4';
    else if (/classic\s*modular|premium|pro/.test(key)) staticId = '5';

    // Prefer sending the dynamic reference when available so the owner preview renders MetaPreviewCard
    if (meta && selectedTemplate && !/^[1-5]$/.test(selectedTemplate)) {
      onTemplateSelect({
        templateId: staticId,
        templateMetaId: selectedTemplate,
        componentKey: meta.componentKey,
        variant: (meta as any)?.variant,
      });
    } else {
      onTemplateSelect(staticId);
    }
  };

  // Exit signup: mark unfinished on server, clear local temp, and go home
  const handleExit = () => {
    // 1) Optimistic: clear local temp and navigate immediately (non-blocking)
    try {
      const email = (userData as any)?.email || '';
      const storageKey = `template_selection:${String(email).toLowerCase()}`;
      const storageCategoryKey = `template_selection_category:${String(email).toLowerCase()}`;
      localStorage.removeItem('signup_progress_v1');
      localStorage.removeItem('template_selection_user');
      localStorage.removeItem(storageKey);
      localStorage.removeItem(storageCategoryKey);
    } catch {}
    try { onBack?.(); } catch {}
    try { if (typeof window !== 'undefined') window.location.hash = ''; } catch {}

    // 2) Background: abandon signup and record lead with a short timeout; ignore failures
    (async () => {
      const timeoutMs = 5000;
      const withTimeout = async (fn: (signal: AbortSignal) => Promise<any>) => {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), timeoutMs);
        try { await fn(ctl.signal); } catch {} finally { clearTimeout(t); }
      };

      // Release reservation or abandon endpoint if we have handle+username
      await withTimeout(async (signal) => {
        try {
          const username = String((userData as any)?.username || '').toLowerCase();
          if (username) {
              const { getSignupSessionId } = await import('../utils/signupSession');
              const { releaseReservation } = await import('../services/api');
              // Release reservation (best effort)
              try { 
                await releaseReservation(String(username).toLowerCase(), getSignupSessionId()); 
              } catch {}
          }
        } catch {}
      });

      // Record incomplete-signup lead for admin follow-up
      await withTimeout(async () => {
        try {
          const { recordIncompleteSignupLead } = await import('../api/leads');
          const { getSignupSessionId } = await import('../utils/signupSession');
          const fullName = String((userData as any)?.name || '').trim();
          const email = String((userData as any)?.email || '').trim();
          const phoneNumber = String((userData as any)?.phone || '').trim();
          const position = String((userData as any)?.position || '').trim();
          const country = String((userData as any)?.location || '').trim();
          const company = String((userData as any)?.company || '').trim();
          const reservedUsername = String((userData as any)?.username || '').trim().toLowerCase();
          const sessionId = getSignupSessionId();
          if (fullName && email) {
            await recordIncompleteSignupLead({ fullName, email, phoneNumber: phoneNumber || undefined, note: 'Exited at template selection', position: position || undefined, country: country || undefined, company: company || undefined, sessionId, reservedUsername: reservedUsername || undefined });
          }
        } catch {}
      });

      // Also free email/id by abandoning any unfinished account
      await withTimeout(async () => {
        try {
          const { getSignupSessionId } = await import('../utils/signupSession');
          const sid = getSignupSessionId();
          const email = String((userData as any)?.email || '').trim().toLowerCase();
          const username = String((userData as any)?.username || '').trim().toLowerCase();
          if (email || username) {
            await fetch('/api/auth/abandon-incomplete', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: email || undefined, username: username || undefined, sessionId: sid }),
            });
          }
        } catch {}
      });
    })();
  };

  const Initials = ({ name }: { name: string }) => {
    const parts = String(name || "").trim().split(/\s+/).slice(0, 2);
    const ii = parts.map((p) => p[0]?.toUpperCase() || "").join("");
    return <div className="h-12 w-12 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">{ii || "U"}</div>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-white">
      {/* Sticky header */}
  <div className="sticky top-0 z-30 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <button onClick={onBack} className="inline-flex items-center text-gray-600 hover:text-gray-900 transition">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Choose Your Template</h1>
          <div className="hidden sm:flex items-center text-xs sm:text-sm text-gray-500">Step 2 of 2</div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8">
        {/* Category tabs */}
        <div className="mb-4 sm:mb-6">
          <div className="inline-flex rounded-xl border bg-white p-1 shadow-sm" role="tablist" aria-label="Template category">
            <button
              role="tab"
              aria-selected={category==='business'}
              onClick={() => { setCategory('business'); setSelectedTemplate(prev => prev); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${category==='business' ? 'bg-indigo-600 text-white shadow' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              Business Cards
            </button>
            <button
              role="tab"
              aria-selected={category==='artist'}
              onClick={() => { setCategory('artist'); setSelectedTemplate(''); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${category==='artist' ? 'bg-indigo-600 text-white shadow' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              Artist Cards
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">Choose a category to preview matching templates.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          {/* User summary + accent controls */}
          <div className="lg:col-span-4">
            <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <Avatar name={userData.name} />
                <div className="min-w-0">
                  <div className="text-base sm:text-lg font-semibold text-gray-900 truncate">{userData.name || "New User"}</div>
                  <div className="text-sm text-gray-600 truncate">
                    {(userData.position || "Your role") + (userData.company ? ` @ ${userData.company}` : "")}
                  </div>
                  <div className="text-xs text-indigo-600 mt-1 truncate">{userData.email}</div>
                </div>
              </div>

              <div className="mt-5">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer select-none">
                  <input type="checkbox" checked={useDefaultAccents} onChange={() => setUseDefaultAccents((v) => !v)} className="h-4 w-4 accent-indigo-600" />
                  Use template default colors
                </label>

                {!useDefaultAccents && (
                  <>
                    <div className="mt-3 flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Palette className="w-4 h-4" />
                      Accent color (override preview)
                    </div>
                    <div className="mt-3 grid grid-cols-8 gap-2">
                      {ACCENTS.map((c) => (
                        <button
                          key={c}
                          onClick={() => setAccentOverride(c)}
                          aria-label={`accent ${c}`}
                          className="h-8 rounded-full border hover:ring-2 hover:ring-offset-1 transition"
                          style={{
                            backgroundColor: c,
                            borderColor: c === accentOverride ? "#111827" : "#e5e7eb",
                            boxShadow: c === accentOverride ? "0 0 0 3px rgba(0,0,0,.15)" : "none",
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Search + grid */}
          <div className="lg:col-span-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search template name or features…"
                  className="w-full rounded-xl border px-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
                />
              </div>
              <div className="text-sm text-gray-500">
                {loading ? 'Loading…' : `${filteredTemplates.length} template${filteredTemplates.length === 1 ? '' : 's'}`}
                {(loadError || usedFallback) && !loading ? (
                  <span className="ml-2 text-amber-600">(offline fallback)</span>
                ) : null}
              </div>
            </div>

            {/* Tiles */}
            {filteredTemplates.length > 0 ? (
              <div
                className={
                  // Both categories: exactly 2 columns per row on larger screens for consistent layout
                  category === 'artist'
                    ? "mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-4 sm:gap-6"
                    : "mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-4 sm:gap-6"
                }
              >
                {filteredTemplates.map((tpl) => {
                const selected = tpl.id === selectedTemplate;
                const meta = serverTemplatesMeta[tpl.id];
                  const rendererStr = String((meta as any)?.renderer || '').toLowerCase();
                  const isDynamic = !!meta && (rendererStr.startsWith('dynamic') || Boolean((meta as any).spec));
                  const variantKey = ensureTemplateVariant((meta?.componentKey || tpl.componentKey) as any, (meta as any)?.variant);
                  const variant = resolveVariant(variantKey || "professional-classic");
                  const accentForTile = useDefaultAccents ? getDefaultAccent(variant) : accentOverride;
                  const cover = (meta as any)?.coverUrl || DEFAULT_COVER;

                return (
                  <div
                    key={tpl.id}
            className={`group relative text-left rounded-2xl border bg-white overflow-hidden shadow-sm hover:shadow-lg transition ${
                        selected ? "ring-2 ring-indigo-400" : ""
                      }`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedTemplate(tpl.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedTemplate(tpl.id); }}
                      style={{ minHeight: 800, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', cursor: 'pointer' }}
                    >
                    {/* Phone preview (contained) */}
                    <div className="bg-gray-50 pt-8 pb-4 overflow-visible">
                      {isPopular(tpl) && (
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 text-amber-600 px-2.5 py-1 text-xs font-semibold shadow-md z-50">
                          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                          Popular
                        </span>
                      )}

                      {/* Lightweight thumbnail for fast grid render */}
                      <div className="lg:h-[680px] md:h-[500px] h-[360px] flex items-center justify-center px-3">
                        <div className={"lg:w-[360px] md:w-[320px] w-[280px]"}>
                          <div className="relative w-full h-0 pb-[200%] overflow-hidden rounded-[2rem] shadow-inner bg-white border">
                            {/* Replace static <img> with lazy live preview when in view */}
                            <LazyPhonePreview
                              meta={meta as any}
                              fallbackCover={(meta as any)?.coverUrl || DEFAULT_COVER}
                              accent={accentForTile}
                              variantKey={variantKey || 'professional-classic'}
                              user={{
                                name: userData.name,
                                position: userData.position,
                                company: userData.company,
                                phone: userData.phone,
                                email: userData.email,
                                website: (userData as any).website,
                                bio: (userData as any).bio,
                                location: userData.location,
                                avatarUrl: normalizeAsset((userData as any).avatar || (userData as any).avatarUrl),
                                coverUrl: normalizeAsset((userData as any).coverImage || (userData as any).coverUrl) || DEFAULT_COVER,
                              }}
                            />
                          </div>
                        </div>
                      </div>

                            {/* diagnostics removed to avoid extra network churn that can cause flicker */}

                      <div className="absolute top-3 right-3 z-50">
                        {selected && (
                          <div className="rounded-full p-2 shadow bg-white" style={{ border: `2px solid ${accentForTile}` }}>
                            <Check className="w-4 h-4" style={{ color: accentForTile }} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tile footer (condensed; solid white panel to prevent image bleed) */}
                    <div className="pt-6 px-4 sm:px-5 pb-6 bg-white border-t"> 
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">{tpl.name}</h3>
                        <div className="flex flex-wrap items-center gap-2 max-w-[48%] justify-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); setInfoOpen(tpl.id); }}
                            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                            aria-label={`Card Info for ${tpl.name}`}
                          >
                            Card Info
                          </button>
                          <button onClick={() => setPeekOpen(tpl.id)} className="text-sm rounded-lg px-3 py-2 border hover:bg-gray-50">View live</button>
                          <button
                            onClick={() => setSelectedTemplate(tpl.id)}
                            className="text-sm font-semibold rounded-lg px-3 py-2 border hover:bg-gray-50"
                            style={{ borderColor: `${accentForTile}66` }}
                          >
                            Use this template
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Full preview dialog (heavy render deferred until opened) */}
                      <TemplatePreviewDialog open={peekOpen === tpl.id} onClose={() => setPeekOpen(null)} title={tpl.name}>
                        {isDynamic && meta ? (
                          <MetaPreviewCard
                            template={meta}
                            user={{
                              name: userData.name,
                              position: userData.position,
                              company: userData.company,
                              phone: userData.phone,
                              email: userData.email,
                              website: (userData as any).website,
                              bio: (userData as any).bio,
                              location: userData.location,
                              avatarUrl: normalizeAsset((userData as any).avatar || (userData as any).avatarUrl),
                              coverUrl: normalizeAsset((userData as any).coverImage || (userData as any).coverUrl) || DEFAULT_COVER,
                            }}
                            accent={accentForTile}
                            mode="full"
                          />
                        ) : (
                          <PreviewCard
                            variant={variant}
                            user={{
                              name: userData.name,
                              position: userData.position,
                              company: userData.company,
                              phone: userData.phone,
                              email: userData.email,
                              website: (userData as any).website,
                              bio: (userData as any).bio,
                              location: userData.location,
                              avatarUrl: normalizeAsset((userData as any).avatar || (userData as any).avatarUrl),
                              coverUrl: normalizeAsset((userData as any).coverImage || (userData as any).coverUrl) || DEFAULT_COVER,
                            }}
                            accent={accentForTile}
                            mode="full"
                            templateMeta={meta ? { componentKey: meta.componentKey, variant: (meta as any)?.variant } : undefined}
                            templateSpec={(meta as any)?.spec || null}
                          />
                        )}
                      </TemplatePreviewDialog>
                      {/* Card Info dialog */}
                      <TemplatePreviewDialog open={infoOpen === tpl.id} onClose={() => setInfoOpen(null)} title={`Card Info — ${tpl.name}`}>
                        <div className="space-y-3">
                          {tpl.description ? (
                            <p className="text-sm text-gray-700">{tpl.description}</p>
                          ) : (
                            <p className="text-sm text-gray-700">This template provides a clean layout optimized for mobile viewing and sharing.</p>
                          )}
                          <div>
                            <div className="text-sm font-semibold text-gray-900 mb-1">Highlights</div>
                            <ul className="space-y-2 text-sm text-gray-700">
                              {["8-color customization", "QR code generation", "Social links integration"].map((it) => (
                                <li key={it} className="flex items-center gap-2">
                                  <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: accentForTile }} />
                                  {it}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </TemplatePreviewDialog>
                  </div>
                );
              })}
              </div>
            ) : (
              <div className="mt-4 sm:mt-6">
                <div className="rounded-2xl border bg-white p-8 text-center text-gray-600">
                  <div className="text-lg font-semibold text-gray-900 mb-1">No templates found</div>
                  <p className="text-sm">{loadError ? loadError : 'There are no active templates in this category yet.'}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom sticky action */}
        <div className="h-24" />
  <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="hidden sm:flex items-center text-sm text-gray-600">
                Selected:&nbsp;
                <strong className="text-gray-900">{uniqueTemplates.find((t) => t.id === selectedTemplate)?.name || "—"}</strong>
              </div>
              <button
                onClick={handleExit}
                className="inline-flex items-center justify-center rounded-xl px-4 py-2 border text-gray-700 hover:bg-gray-50"
              >
                Exit signup
              </button>
              <button
                onClick={handleContinue}
                disabled={!selectedTemplate}
                className="ml-auto inline-flex items-center justify-center rounded-xl px-5 sm:px-7 py-3 bg-indigo-600 text-white font-semibold shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mt-2">You can change your template anytime after creating your card.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* local helper for initials in this file */
const Avatar: React.FC<{ name?: string }> = ({ name }) => (
  <div className="h-12 w-12 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600 font-bold">
    {initials(name)}
  </div>
);

function initials(name?: string) {
  const parts = String(name || "U").trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "U";
}

// Lazy phone preview: renders image until tile enters viewport, then renders actual PreviewCard with spec/variant
const LazyPhonePreview: React.FC<{
  meta?: TemplateMeta;
  fallbackCover: string;
  accent: string;
  variantKey: string;
  user: any;
}> = ({ meta, fallbackCover, accent, variantKey, user }) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = React.useState(false);
  const [hasRendered, setHasRendered] = React.useState(false);

  React.useEffect(() => {
    if (hasRendered) return; // don’t unmount once rendered
    const el = ref.current;
    if (!el || typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setInView(true);
          break;
        }
      }
    }, { root: null, rootMargin: '100px', threshold: 0.05 });
    obs.observe(el);
    return () => { try { obs.disconnect(); } catch {} };
  }, [hasRendered]);

  React.useEffect(() => {
    if (inView) setHasRendered(true);
  }, [inView]);

  return (
    <div ref={ref} className="absolute inset-0">
      {!hasRendered ? (
        <img src={fallbackCover} alt="template preview" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      ) : (
        <PreviewCard
          variant={resolveVariant(variantKey || 'professional-classic')}
          user={user}
          accent={accent}
          mode="grid"
          templateMeta={meta ? { componentKey: meta.componentKey, variant: (meta as any)?.variant } : undefined}
          templateSpec={(meta as any)?.spec || null}
        />
      )}
    </div>
  );
};
